import { DatePicker } from 'antd';
import { useFormik } from 'formik';
import { Button, InputBar } from '~/ui';
import styles from './TournamentGeneral.module.scss';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import TournamentConditions from './TournamentConditions/TournamentConditions';
import { validate } from '~/utils/validateTournament';
import { useContext, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastError } from '~/utils/useToastError';
import { QuerySelect } from '~/ui';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import { AccommodationItem } from '~/components/AccommodationForm';
import cn from 'classnames';
import {
  getAccommodationById,
  getAccommodationsInfinite,
  getAssetById,
  getAssetsInfinite,
  getOrganizationsInfinite,
} from '~/utils/api/queries';

export interface ITournamentCreateData {
  name: string;
  game_id: number;
  start_date: string;
  end_date: string;
  color: string;
}
export interface ITournamentUpdateProps {
  tournament: {
    name: string;
    game_id: number;
    start_date: string;
  };
  conditions: {
    from_placed: number;
    to_placed: number;
    points: number;
  }[];
}

export interface IFormikCondition {
  id: number;
  points: number;
  place: string;
}

interface IFormik {
  assets: number[];
  name: string;
  date: {
    startDate: dayjs.Dayjs | null;
    endDate: dayjs.Dayjs | null;
  };
  color?: {};
  conditions: IFormikCondition[];
  organizations: number[];
  accommodations: number[];
}

interface ITournamentGeneralProps {
  isEdit?: boolean;
  createTournament: (data: ITournamentUpdateProps) => void;
  updateTournament: (params: {
    tournamentId: number;
    data: ITournamentCreateData;
    paginate: string;
  }) => void;
  id: number;
}

interface ITournament {
  id: number;
  name: string;
  accommodations: {
    accommodation: {
      id: number;
      name: string;
    };
  }[];
  assets: {
    asset: {
      id: number;
      name: string;
    };
  }[];
  organizations: {
    organization: {
      id: number;
      name: string;
    };
  }[];
  start_date: string;
  end_date: string;
  status: {
    id: number;
    name: string;
    type: string;
  };
  conditions: {
    id: number;
    points: number;
    from_placed: number;
    to_placed: number;
  }[];
}

const TournamentGeneral = ({
  isEdit = false,
  createTournament,
  updateTournament,
  id,
}: ITournamentGeneralProps) => {
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const { t } = useTranslation();
  const { tournamentId, id: entertainmentId } = useParams();
  const [searchParams] = useSearchParams();
  const paginate = searchParams.get('paginate');
  const toastSuccess = useToastSuccess();
  const toastError = useToastError();
  const client = useQueryClient();
  const navigate = useNavigate();

  const handleDataSubmit = (values: IFormik) => {
    const data = {
      tournament: {
        name: values.name,
        game_id: id,
        start_date: values.date?.startDate?.format('YYYY-MM-DD') || '',
        end_date: values.date?.endDate?.format('YYYY-MM-DD') || '',
        color: '#FF5733',
        asset_ids: values.assets,
        accommodation_ids: values.accommodations,
        organization_ids: values.organizations,
      },
      conditions: values.conditions.map(item => {
        const places = String(item.place).split('-');

        return {
          from_placed: Number(places[0].trim()),
          to_placed: places[1]
            ? Number(places[1].trim())
            : Number(places[0].trim()),
          points: Number(item.points),
        };
      }),
    };
    if (isEdit) {
      updateTournament({
        tournamentId: Number(tournamentId),
        data: data.tournament,
        paginate: paginate || '',
      });
    } else {
      createTournament(data);
    }
  };

  const { data: asset, isLoading: isAssetLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/assets');
      return data.data;
    },
    queryKey: ['assets'],
    enabled: !isSuperAdmin,
    onError: error => {
      toastError(error);
    },
  });

  const {
    values,
    handleSubmit,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setValues,
    isValid,
  } = useFormik<IFormik>({
    initialValues: {
      assets: [] as number[],
      organizations: [] as number[],
      accommodations: [] as number[],
      name: '',
      date: {
        startDate: null,
        endDate: null,
      },
      color: {},
      conditions: [],
    },
    validate,
    onSubmit: () => {
      handleDataSubmit(values);
    },
  });


  const { data: tournament, isLoading } = useQuery<ITournament>({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/game-tournament/${tournamentId}`
      );
      return data;
    },
    queryKey: ['tournament', tournamentId],
    enabled: isEdit,
  });

  const status = tournament?.status?.id;

  const selectedAssetIds = values.assets;

  const { mutateAsync: deleteCondition } = useMutation(
    async (conditionId: number) => {
      const { data } = await instance.delete(
        `admin/game-tournament-condition/${conditionId}`
      );
      return data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('condition_successful_deleted'));
        client.invalidateQueries({ queryKey: ['tournament', tournamentId] });
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  useEffect(() => {
    if (!isSuperAdmin && asset && !isAssetLoading) {
      setFieldValue('assets', asset.map((a: { id: number }) => a.id));
    }
  }, [asset]);

  useEffect(() => {
    if (!values.assets.length) {
      setFieldValue('accommodations', []);
    }
  }, [values.assets]);

  useEffect(() => {
    if (!values.accommodations.length || !values.assets.length) {
      setFieldValue('organizations', []);
    }
  }, [values.accommodations, values.assets]);

  useEffect(() => {
    if (isEdit && !isLoading && tournamentId && tournament) {
      const conditions = tournament.conditions.map(item => {
        return {
          id: item.id,
          points: item.points,
          place:
            item.from_placed === item.to_placed
              ? String(item.from_placed)
              : `${item.from_placed}-${item.to_placed}`,
        };
      });

      const assets = tournament?.assets?.map(item => item.asset.id) ?? [];
      const accommodations = tournament?.accommodations?.map(item => item.accommodation.id) ?? [];
      const organizations = tournament?.organizations?.map(item => item.organization.id) ?? [];

      setValues({
        assets,
        organizations,
        accommodations,
        name: tournament?.name,
        date: {
          startDate: dayjs(tournament.start_date),
          endDate: dayjs(tournament.end_date),
        },
        conditions,
      });
    }
  }, [tournament, isEdit, isLoading, tournamentId]);

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.infoWrapper}>
          <div
            className={cn(
              styles.nameWrapper,
              isSuperAdmin && styles.superAdminWrapper
            )}
          >
            <InputBar
              name="name"
              value={values.name}
              label={t('name_secondary')}
              placeholder={t('game_name')}
              onChange={e => setFieldValue('name', e.target.value)}
              disabled={status === 1 || status === 3}
              errors={errors.name}
              onBlur={handleBlur}
              error={touched.name && Boolean(errors.name)}
            />
          </div>
          {isSuperAdmin && (
            <div
              className={cn(
                styles.assetWrapper,
                isSuperAdmin && styles.superAdminWrapper
              )}
            >
              <QuerySelect
                label={t('asset')}
                placeholder={t('asset_placeholder')}
                disabled={status === 1 || status === 3}
                multiple
                star={false}
                queryFn={({ pageParam, meta }) =>
                  getAssetsInfinite({
                    page: pageParam,
                    perPage: 15,
                    query: meta?.searchQuery as string,
                    game_id: id,
                  })
                }
                queryKey={['assets-tournament', id]}
                getItems={getAssetById}
                value={values.assets}
                emptyText={t('nothing_found')}
                onChange={(value: number[]) => {
                  setFieldValue('assets', value);
                  const filteredAccommodations = values.accommodations.filter(accId => {
                    const cached = client.getQueryData<AccommodationItem>(
                      [['accommodations-tournament', id], 'item', accId]
                    );
                    return cached ? value.includes(cached.asset_id!) : true;
                  });
                  setFieldValue('accommodations', filteredAccommodations);
                  if (filteredAccommodations.length === 0) {
                    setFieldValue('organizations', []);
                  } else {
                    getOrganizationsInfinite({ game_id: id, accommodation_ids: filteredAccommodations }).then(data => {
                      const validOrgIds = new Set(data.data.map(o => o.id));
                      setFieldValue('organizations', values.organizations.filter(orgId => validOrgIds.has(orgId)));
                    });
                  }
                }}
              />
            </div>
          )}
          <div
            className={cn(
              styles.dateWrapper,
              isSuperAdmin && styles.superAdminWrapper
            )}
          >
            <span className={styles.dateLabel}>{t('date')}</span>
            <DatePicker.RangePicker
              className={`${styles.datepicker} ${
                touched.date?.startDate && errors.date?.startDate
                  ? styles.error
                  : ''
              }`}
              format="DD.MM.YYYY"
              value={[values.date.startDate, values.date.endDate]}
              disabled={status === 1 || status === 3}
              onChange={date => {
                if (date && date[0] && date[1]) {
                  setFieldValue('date.startDate', date[0]);
                  setFieldValue('date.endDate', date[1]);
                } else {
                  setFieldValue('date.startDate', null);
                  setFieldValue('date.endDate', null);
                }
              }}
              onBlur={() => {
                setFieldTouched('date.startDate', true);
                setFieldTouched('date.endDate', true);
              }}
              placeholder={[t('start_date'), t('end_date')]}
              disabledDate={current =>
                current && current.isBefore(dayjs(), 'day')
              }
            />
            {touched.date?.startDate &&
              errors.date?.startDate &&
              touched.date?.endDate &&
              errors.date?.endDate && (
                <span className={styles.errorText}>
                  {errors.date.startDate}
                </span>
              )}
          </div>
        </div>
        <div className={styles.objectsWrapper}>
          <QuerySelect
            label={t('accommodations')}
            placeholder={t('select_accommodation')}
            value={values.assets.length === 0 ? [] : values.accommodations}
            queryFn={({ pageParam, meta }) =>
              getAccommodationsInfinite({
                sort: '',
                field: '',
                asset_ids: selectedAssetIds,
                game_id: id,
                page: pageParam,
                perPage: 15,
                query: meta?.searchQuery as string,
              })
            }
            queryKey={['accommodations-tournament', id]}
            updateOn={selectedAssetIds.join(',')}
            getItems={getAccommodationById}
            multiple
            star={true}
            noCode
            emptyText={t('nothing_found')}
            disabled={values.assets.length === 0 || status === 1 || status === 3}
            onChange={(value: number[]) => {
              setFieldValue('accommodations', value);
              if (value.length === 0) {
                setFieldValue('organizations', []);
                return;
              }
              getOrganizationsInfinite({ game_id: id, accommodation_ids: value }).then(data => {
                const validOrgIds = new Set(data.data.map(o => o.id));
                setFieldValue('organizations', values.organizations.filter(orgId => validOrgIds.has(orgId)));
              });
            }}
            error={errors.accommodations as string}
            className={styles.select}
          />
          <QuerySelect
            label={t('organizations')}
            placeholder={t('select_least_one_organization')}
            value={values.organizations}
            queryFn={({ pageParam, meta }) =>
              getOrganizationsInfinite({
                game_id: id,
                page: pageParam,
                perPage: 15,
                query: meta?.searchQuery as string,
              })
            }
            queryKey={['organizations-tournament', id]}
            multiple
            star={true}
            emptyText={t('nothing_found')}
            disabled={!values.accommodations.length || !values.assets.length || status === 1 || status === 3}
            onChange={(value: number[]) => setFieldValue('organizations', value)}
            error={errors.organizations as string}
            className={styles.select}
          />
        </div>
        <div className={styles.colorWrapper}></div>
        <div>
          <TournamentConditions
            status={status}
            tournamentId={tournamentId || ''}
            isEdit={isEdit}
            conditions={values.conditions}
            setFieldValue={setFieldValue}
            handleDeleteCondition={deleteCondition}
          />
          {errors.conditions && (
            <span className={styles.conditionsError}>
              {errors.conditions as string}
            </span>
          )}
        </div>
        {status !== 1 && status !== 3 ? (
          <Button
            text={t('save_n_exit')}
            type="submit"
            className={styles.button}
            disabled={!isValid}
          />
        ) : (
          <Button
            text={t('back')}
            type="button"
            className={styles.button}
            onClick={() =>
              navigate(
                `/entertainment/${entertainmentId}?activeTab=tournaments${
                  paginate ? `&paginate=${paginate}` : ''
                }`
              )
            }
          />
        )}
      </form>
    </div>
  );
};

export default TournamentGeneral;
